/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */


 /**
 * Dynamic Variant Hiding Solution
 * 
 * This script hides specific variant options based on product tags.
 * It works with the Variant Image Wizard + Swatch app.
 * 
 * Usage: Add product tags in the format "hide-variant:Color:Value"
 * Example: "hide-variant:Colour:Navy Blue" will hide the Navy Blue color option
 * Examples:
 * hide-variant:Colour:Navy Blue - Hides the Navy Blue colour option
 * hide-variant:Size:XS - Hides the XS size option
 * hide-variant:Material:Leather - Hides the Leather material option


 */

// Wait for DOM content to be loaded
document.addEventListener('DOMContentLoaded', function() {
    // Only run on product pages
    if (window.location.pathname.includes('/products/')) {
      // Wait for app to initialize
      setTimeout(function() {
        // Initial hiding of variants
        hideVariantsBasedOnTags();
        relabelRioOptionTitles();
        
        // Set up mutation observer to detect variant changes
        setupVariantObserver();
        
        // Fallback for Symmetry events
        document.addEventListener('on:variant:change', function(event) {
          hideVariantsBasedOnTags();
          relabelRioOptionTitles();
        });
        
        document.addEventListener('on:quickbuy:after-open', function(event) {
          setTimeout(hideVariantsBasedOnTags, 300);
          setTimeout(relabelRioOptionTitles, 300);
        });
      }, 1000);
    }
  });
  
  // Set up a mutation observer to detect changes to the variant selectors
  function setupVariantObserver() {
    const optionsContainer = document.querySelector('.rio-options');
    
    if (optionsContainer) {
      const observer = new MutationObserver(function(mutations) {
        hideVariantsBasedOnTags();
        relabelRioOptionTitles();
      });
      
      observer.observe(optionsContainer, { 
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'data-option-value']
      });
      
      console.log('Variant observer set up for rio-options');
    } else {
      console.log('Rio-options container not found, trying again in 2 seconds');
      setTimeout(setupVariantObserver, 2000);
    }
  }
  
  // Get product tags for the current product
  function getProductTags() {
    // Try different methods to find product data with tags
    
    // Method 1: Check Variant Wizard's product data
    if (window.__productWizRioProduct && window.__productWizRioProduct.tags) {
      return parseProductTags(window.__productWizRioProduct.tags);
    }
    
    // Method 2: Look in window object for product data
    for (let key in window) {
      if (key.toLowerCase().includes('product')) {
        try {
          const potentialData = window[key];
          if (potentialData && potentialData.tags) {
            return parseProductTags(potentialData.tags);
          }
        } catch (e) {
          continue; // Skip if property can't be accessed
        }
      }
    }
    
    // Method 3: Check meta tags in the DOM
    const metaTag = document.querySelector('meta[property="og:product:tag"]');
    if (metaTag) {
      return parseProductTags(metaTag.content);
    }
    
    // Method 4: Look for tags in JSON-LD
    const jsonLdScripts = document.querySelectorAll('script[type="application/ld+json"]');
    for (let i = 0; i < jsonLdScripts.length; i++) {
      try {
        const data = JSON.parse(jsonLdScripts[i].textContent);
        if (data && data.tags) {
          return parseProductTags(data.tags);
        }
        // Check nested structures
        if (data && data['@graph']) {
          for (let j = 0; j < data['@graph'].length; j++) {
            if (data['@graph'][j].tags) {
              return parseProductTags(data['@graph'][j].tags);
            }
          }
        }
      } catch (e) {
        continue;
      }
    }
    
    // Fallback: Default to an array with Navy Blue (can be removed when tags are working)
    console.log('No product tags found, using default Navy Blue fallback');
    return [{ option: 'Colour', value: 'Navy Blue' }];
  }
  
  // Parse product tags to find hide-variant instructions
  function parseProductTags(tags) {
    const hideInstructions = [];
    
    // If tags is a string, convert it to an array
    if (typeof tags === 'string') {
      tags = tags.split(', ');
    }
    
    // If we have no tags or tags isn't an array, return empty instructions
    if (!tags || !Array.isArray(tags)) {
      return hideInstructions;
    }
    
    // Look for tags that match our format: hide-variant:Option:Value
    tags.forEach(function(tag) {
      if (typeof tag === 'string' && tag.startsWith('hide-variant:')) {
        const parts = tag.split(':');
        if (parts.length === 3) {
          hideInstructions.push({
            option: parts[1],
            value: parts[2]
          });
          console.log('Found hide-variant tag:', parts[1], parts[2]);
        }
      }
    });
    
    return hideInstructions;
  }
  
  // Hide variants based on product tags
  function hideVariantsBasedOnTags() {
    try {
      // Get hide instructions from product tags
      const hideInstructions = getProductTags();
      
      if (hideInstructions.length === 0) {
        console.log('No variants to hide');
        return;
      }
      
      // Find all option containers
      const optionContainers = document.querySelectorAll('[data-option][aria-label]');
      let hiddenCount = 0;
      
      optionContainers.forEach(function(container) {
        // Get the option name
        const optionName = container.getAttribute('aria-label');
        
        // Find instructions for this option
        const instructionsForThisOption = hideInstructions.filter(
          instruction => instruction.option === optionName
        );
        
        // Apply each instruction
        instructionsForThisOption.forEach(function(instruction) {
          // Find all elements with this value
          const elements = container.querySelectorAll('[data-option-value="' + instruction.value + '"]');
          
          elements.forEach(function(element) {
            // Find the parent grid item to hide
            let parent = element;
            
            while (parent && 
                   !parent.classList.contains('PwzrGrid-item-113') && 
                   !parent.classList.contains('PwzrGrid-item-354') &&
                   parent !== container) {
              parent = parent.parentNode;
              if (!parent || parent === document.body) break;
            }
            
            // Hide the element if we found a valid parent
            if (parent && 
                parent !== container && 
                (parent.classList.contains('PwzrGrid-item-113') || 
                 parent.classList.contains('PwzrGrid-item-354'))) {
              console.log('Hiding variant:', instruction.option, instruction.value);
              parent.style.display = 'none';
              hiddenCount++;
            }
          });
        });
      });
      
      console.log('Total variants hidden:', hiddenCount);
      
    } catch (error) {
      console.warn('Error in hideVariantsBasedOnTags:', error);
    }
  }

  /**
   * Rio / Variant Image Wizard option labels
   *
   * Cin7 syncs option names into Shopify and overwrites Admin edits on each
   * sync, so we cannot rename options in Shopify. Rio injects
   * .rio-product-option-title after page load, so we remap those visible
   * titles only (data-option / aria-label stay as Cin7 sent them).
   *
   * Shoes: Shoe Size (US/EU) in the US market, otherwise Shoe Size (AU/EU)
   * Clothing: Size or Clothing Size → Clothing Size (AU)
   */
  function getRioProductTags() {
    var tags = [];
    if (window.__productWizRioProduct && window.__productWizRioProduct.tags) {
      tags = window.__productWizRioProduct.tags;
    }
    if (typeof tags === 'string') {
      tags = tags.split(',').map(function (tag) {
        return tag.trim();
      });
    }
    if (!Array.isArray(tags)) {
      return [];
    }
    return tags.map(function (tag) {
      return String(tag).toLowerCase();
    });
  }

  function getRioProductClassification() {
    var config = window.sheWearOptionLabels || {};
    var tags = getRioProductTags();
    var isShoes = !!config.isShoes;
    var isClothing = !!config.isClothing;

    if (tags.indexOf('shoes') !== -1 || tags.indexOf('shoe') !== -1) {
      isShoes = true;
    }
    if (tags.indexOf('clothing') !== -1) {
      isClothing = true;
    }

    return { isShoes: isShoes, isClothing: isClothing };
  }

  function isUsMarket() {
    var config = window.sheWearOptionLabels || {};
    var country = '';
    if (window.Shopify && window.Shopify.country) {
      country = String(window.Shopify.country).toUpperCase();
    } else if (config.country) {
      country = String(config.country).toUpperCase();
    }
    var handle = String(config.marketHandle || '').toLowerCase();

    if (country === 'US') return true;
    if (handle === 'us' || handle === 'united-states') return true;
    return !!config.isUsMarket;
  }

  function getRioDisplayOptionName(originalName) {
    if (!originalName) return null;

    var normalized = originalName.toLowerCase().trim();
    var shoeLabel = isUsMarket() ? 'Shoe Size (US/EU)' : 'Shoe Size (AU/EU)';
    var classification = getRioProductClassification();

    if (normalized.indexOf('shoe size') !== -1) {
      return shoeLabel;
    }

    if (normalized.indexOf('clothing size') !== -1) {
      return 'Clothing Size (AU)';
    }

    if (normalized !== 'size') {
      return null;
    }

    if (classification.isShoes) {
      return shoeLabel;
    }
    if (classification.isClothing) {
      return 'Clothing Size (AU)';
    }

    return null;
  }

  function relabelRioOptionTitles() {
    var titles = document.querySelectorAll('.rio-product-option-title');
    titles.forEach(function (titleEl) {
      var textNode = Array.prototype.find.call(titleEl.childNodes, function (node) {
        return node.nodeType === Node.TEXT_NODE && node.textContent.trim();
      });
      if (!textNode) return;

      if (!titleEl.dataset.swOriginalOption) {
        var rawLabel = textNode.textContent;
        var suffixMatch = rawLabel.match(/(\s*:\s*)$/);
        titleEl.dataset.swOriginalOption = rawLabel.replace(/:\s*$/, '').trim();
        titleEl.dataset.swOptionSuffix = suffixMatch ? suffixMatch[1] : '';
      }

      var displayName = getRioDisplayOptionName(titleEl.dataset.swOriginalOption);
      if (!displayName) return;

      var nextText = displayName + titleEl.dataset.swOptionSuffix;
      if (textNode.textContent !== nextText) {
        textNode.textContent = nextText;
      }
    });
  }

  function setupRioOptionLabelObserver() {
    if (window.__swRioOptionLabelObserverReady) return;
    window.__swRioOptionLabelObserverReady = true;

    var observeRioRoots = function () {
      document.querySelectorAll('.rio-options').forEach(function (rio) {
        if (rio.dataset.swOptionLabelObserved) return;
        rio.dataset.swOptionLabelObserved = 'true';
        new MutationObserver(function () {
          relabelRioOptionTitles();
        }).observe(rio, { childList: true, subtree: true });
      });
      relabelRioOptionTitles();
    };

    observeRioRoots();

    var bodyObserver = new MutationObserver(function () {
      observeRioRoots();
    });
    bodyObserver.observe(document.documentElement, { childList: true, subtree: true });
    setTimeout(function () {
      bodyObserver.disconnect();
    }, 20000);
  }

  document.addEventListener('DOMContentLoaded', function () {
    setupRioOptionLabelObserver();
    setTimeout(relabelRioOptionTitles, 1000);
    setTimeout(relabelRioOptionTitles, 3000);
  });

  document.addEventListener('on:quickbuy:after-open', function () {
    setTimeout(relabelRioOptionTitles, 300);
  });